import { getBaseMintNftEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { FeeType, type MinimalDrop } from "@anaconda/core/drop/types"
import {
  type MintRequest,
  mintFactoryReatom,
  parseUnionAddress,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import type { Address } from "viem"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "../../blockchains/blockchain-balance-sdk-provider.reatom"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { InsufficientFundsError } from "../../errors"
import { MintToast } from "../../toasts/mint-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

export interface MintActionParams {
  drop: MinimalDrop
}

export interface MintActionResult {
  txId: Address
  drop: MinimalDrop
}

export const MintTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { address: addressAtom } = mol(WalletConnectMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)

  const mintAction = action(async ({ drop }: MintActionParams) => {
    const container = sdkContainerAtom()
    const balanceSDK = balanceSDKAtom()
    const address = addressAtom()

    const { id, blockchain } = drop
    // In V0 drops, there is only one phase
    const currentPhase = drop.phases[0]

    // Get balance for the specific blockchain of the drop
    let balance: string | undefined

    if (address && balanceSDK) {
      try {
        balance = await wrap(
          balanceSDK.getNativeBalance({
            blockchain,
            address: address as Address,
          }),
        )
      } catch (e) {
        console.error("🚀 MINT MUTATION: Balance SDK failed:", e)
        balance = undefined
      }
    }

    if (balance && currentPhase) {
      // Calculate fees
      const fees = drop.fees.reduce((acc, fee) => {
        const feeValue = fee.type === FeeType.FLAT ? fee.amount : 0
        return acc + feeValue
      }, 0)

      // For drops, currentPhase.price.amount is the total price including fees
      // So we need to calculate the base price by subtracting fees
      const totalPrice = currentPhase.price.amount
      const basePrice = Big(totalPrice).minus(fees).toString()

      if (balance === "0" || Big(balance).lt(totalPrice)) {
        throw new InsufficientFundsError({
          requiredAmount: totalPrice,
          currentBalance: balance,
          currency: currentPhase.price.currency,
          blockchain,
          basePrice,
          fees: fees.toString(),
          quantity: 1,
        })
      }
    }

    // Execute mint via Reatom SDK
    const mint = mintFactoryReatom(container)
    const collectionId = parseUnionAddress(id).address
    const request: MintRequest = {
      blockchain,
      collectionId,
    }

    const result = await wrap(mint(request))

    return { txId: result.txId, drop }
  }, "mintAction").extend(
    withAsync(),

    // onCall: Called when action starts (replaces onMutate)
    withCallHook(async (values, [params]) => {
      const { drop } = params

      sonnerToast.custom((id) => <MintToast.Pending drop={drop} id={id} />, {
        duration: Number.POSITIVE_INFINITY,
      })

      const baseTransactionEventContext: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
      }

      logger?.api.pushEvent(
        TransactionEvent.Started,
        baseTransactionEventContext,
      )
      amplitude.track(TransactionEvent.Started, baseTransactionEventContext)
    }),
  )

  // onFulfill: Called on success (replaces onSuccess)
  mintAction.onFulfill.extend(
    withCallHook((result, [params]) => {
      const { txId, drop } = result.payload

      const baseTransactionEventContext: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
      }

      const context = {
        ...baseTransactionEventContext,
        transactionId: txId,
      }

      logger?.api.pushEvent(TransactionEvent.Completed, context)
      amplitude.track(TransactionEvent.Completed, context)

      sonnerToast.dismiss()
      // sonnerToast.custom((id) => <MintToast.Success drop={drop} id={id} />)
    }),
  )

  // onReject: Called on error (replaces onError)
  mintAction.onReject.extend(
    withCallHook(({ error, params: [paramsValue] }) => {
      console.error("Mint failed:", error)

      const { drop } = paramsValue

      const baseTransactionEventContext: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
      }

      const context = {
        ...baseTransactionEventContext,
        error: getTransactionErrorMessage(error),
      }

      logger?.api.pushEvent(TransactionEvent.Failed, context)
      amplitude.track(TransactionEvent.Failed, context)

      sonnerToast.dismiss()
      sonnerToast.custom((id) => (
        <MintToast.Error drop={drop} id={id} error={error as Error} />
      ))
    }),
  )

  return { mintAction }
})
